/*
 * ============================================================
 * MARKETPLACE PRODUCT — assets/ccs-product.js
 * ============================================================
 * Enhances the theme's product page for CustomDesign listings.
 *
 * Deliberately an enhancement, not a replacement. The theme's variant picker
 * already resolves the selected variant, updates price and image, and drives
 * the cart form and sticky add-to-cart. Re-implementing that would mean two
 * copies to maintain and a second set of bugs.
 *
 * So this does four things on top:
 *
 *   1. Replaces the Color option's buttons with hex swatches, and forwards a
 *      swatch click into the real radio so the theme reacts as usual.
 *   2. Opens the size axis on Large.
 *   3. Shows the front and back of whichever color is selected — each color
 *      has its own design, so both shots change with it.
 *   4. Checks stock against SIB, where it actually lives.
 *
 * The theme's own radios stay in the DOM and stay authoritative. They are
 * visually hidden, never removed: the form posts from them, and `variants.js`
 * reads them to work out the variant.
 */
(function (window, document) {
  'use strict';

  var COLOR_OPTION = 'Color';
  var SIZE_OPTION = 'Size';

  var root, listing, colorsByKey = {};
  var selected = { color: null };

  function $(selector, scope) { return (scope || document).querySelector(selector); }
  function $$(selector, scope) { return Array.prototype.slice.call((scope || document).querySelectorAll(selector)); }
  function el(name) { return $('[data-ccsp="' + name + '"]', root); }

  // ── The theme's radios ────────────────────────────────────────────────────

  function radios(optionName) {
    return $$('input.product-form__radio[name="' + optionName + '"]');
  }

  /*
   * Pick an option value through the theme's own control.
   *
   * Setting .checked alone is not enough: variants.js listens for 'change' to
   * recompute the variant, price, image and cart form. Dispatching it is what
   * makes a swatch click behave exactly like a click on the original button.
   */
  function selectOption(optionName, value) {
    var inputs = radios(optionName);
    var match = inputs.filter(function (input) {
      return String(input.value).trim().toLowerCase() === String(value).trim().toLowerCase();
    })[0];
    if (!match || match.checked) return !!match;

    match.checked = true;
    match.dispatchEvent(new Event('change', { bubbles: true }));
    return true;
  }

  function currentOption(optionName) {
    var checked = radios(optionName).filter(function (input) { return input.checked; })[0];
    return checked ? checked.value : null;
  }

  // ── Color ────────────────────────────────────────────────────────────────

  /*
   * The theme renders Color as a row of text buttons. For a listing with
   * eight colorways that is a wall of words where a shopper expects swatches,
   * so the fieldset is hidden and the swatches below stand in for it.
   *
   * Hidden rather than removed: the radios are what the form posts.
   */
  function hideThemeColorFieldset() {
    var input = radios(COLOR_OPTION)[0];
    if (!input) return;
    var fieldset = input.closest('fieldset') || input.closest('.product-form__input');
    if (fieldset) fieldset.classList.add('ccsp-hidden-option');
  }

  function colorFor(value) {
    // Swatches key on the color KEY (the SIB child handle); the theme's radios
    // carry the human NAME, since that is the Shopify option value.
    var found = null;
    (listing.colors || []).forEach(function (color) {
      if (String(color.name).toLowerCase() === String(value).toLowerCase()) found = color;
    });
    return found;
  }

  function pickColor(colorKey, options) {
    var color = colorsByKey[colorKey];
    if (!color) return;

    var opts = options || {};
    selected.color = colorKey;

    // Forward into the theme unless this call came FROM the theme, which would
    // otherwise bounce the change back and forth.
    if (!opts.fromTheme) selectOption(COLOR_OPTION, color.name);

    renderSwatches();
    /*
     * Not on the first pick. The gallery already opens on the feature photo --
     * a model wearing this design in the default color, which is the picture
     * the page is meant to lead with -- and moving to the flat front shot
     * would skip straight past it.
     *
     * On a colour the shopper CHOSE, landing on its front is the point: the
     * gallery would otherwise stay on whatever slide index it was on, which
     * after a switch is the back of the previous colour.
     */
    if (!opts.initial) showFirstForColor();
    // Sizes available differ by color: a color that does not come in 3XL has
    // no variant for it, and the theme greys it out on its own once the color
    // changes. Re-asserting the default keeps a now-unavailable size from
    // staying selected.
    ensureSize();
    checkStock();
  }

  function renderSwatches() {
    $$('[data-ccsp="swatch"]', root).forEach(function (button) {
      var on = button.dataset.color === selected.color;
      button.classList.toggle('is-selected', on);
      button.setAttribute('aria-checked', on ? 'true' : 'false');
    });
    var color = colorsByKey[selected.color];
    var name = el('color-name');
    if (name && color) name.textContent = color.name;
  }

  /*
   * Put the gallery on the first picture this color has.
   *
   * Not the front shot specifically. The default color also has the feature
   * photo -- a model wearing the design -- and that sits first in its group,
   * so "the front" would skip the picture the seller chose to lead with. The
   * first thumbnail is whatever the theme's own grouping put there, which is
   * the same answer for every color without this having to know which of them
   * is the default.
   *
   * Deferred two frames because the theme filters the gallery off the change
   * event this follows, and the thumbnails are the previous color's until it
   * has.
   */
  function showFirstForColor() {
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        var thumbs = $$('.productView-thumbnail').filter(function (el) {
          return el.offsetParent !== null && !el.closest('.slick-cloned');
        });
        var first = thumbs[0] && thumbs[0].querySelector('img, .productView-thumbnail-link');
        if (first) { first.click(); return; }

        // No thumbnails on this layout: fall back to the front shot.
        var color = colorsByKey[selected.color];
        if (color) showInGallery(color.front || color.back);
      });
    });
  }

  /*
   * Point the theme's gallery at one of this color's shots.
   *
   * Both images are already product media — the publisher uploads front and
   * back per color — so this finds the matching slide rather than injecting
   * an image the gallery does not know about. Matching on the filename because
   * Shopify rewrites the CDN URL and its query string.
   */
  function showInGallery(url) {
    if (!url) return;
    var name = url.split('/').pop().split('?')[0];
    var slides = $$('.product-single__media img, .productView-image img, .product__media img');

    for (var i = 0; i < slides.length; i++) {
      var src = slides[i].getAttribute('src') || slides[i].getAttribute('data-src') || '';
      if (src.indexOf(name) === -1) continue;

      var slide = slides[i].closest('[data-media-id], .item, .slick-slide');
      if (!slide) return;

      // The theme's thumbnails are the supported way to move the gallery; a
      // programmatic click keeps whatever slider it happens to be using in
      // step, rather than reaching into the slider's internals.
      var mediaId = slide.getAttribute('data-media-id');
      var thumb = mediaId && $('[data-media-id="' + mediaId + '"] img, [data-target="' + mediaId + '"]',
        $('.product-single__thumbnail, .productView-thumbnail, .thumbnail-list') || document);
      if (thumb) { thumb.click(); return; }

      slides[i].scrollIntoView({ block: 'nearest', inline: 'center', behavior: 'smooth' });
      return;
    }
  }

  // ── Size ──────────────────────────────────────────────────────────────────

  /*
   * Open on Large.
   *
   * Most shoppers are an L, so a size axis that starts on Small means the
   * commonest case begins with a correction. Applied only while nothing
   * deliberate is selected — once a shopper has chosen, this must not override
   * them on a color change.
   */
  function ensureSize() {
    var inputs = radios(SIZE_OPTION);
    if (!inputs.length) return;

    var current = currentOption(SIZE_OPTION);
    var currentInput = inputs.filter(function (i) { return i.checked; })[0];
    var currentUsable = currentInput && !currentInput.disabled;

    if (current && currentUsable && ensureSize.touched) return;

    var preferred = (root.dataset.defaultSize || 'L').toUpperCase();
    var target = inputs.filter(function (input) {
      return String(input.value).toUpperCase() === preferred && !input.disabled;
    })[0];

    // No Large on this listing, or it is unavailable in this color: fall back
    // to the first size that can actually be bought.
    if (!target) target = inputs.filter(function (input) { return !input.disabled; })[0];
    if (target && !target.checked) {
      target.checked = true;
      target.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  // ── Stock ─────────────────────────────────────────────────────────────────

  /*
   * Stock lives on SIB, not here.
   *
   * These variants are published untracked with inventoryPolicy CONTINUE, so
   * Shopify will happily sell one that cannot be made. The listing metafield
   * carries the GTIN of every (color, size), which is what SIB knows them by.
   *
   * Advisory for now: it reports rather than blocks, because a failed check
   * must not stop a sale that would have been fine.
   */
  // The SIB style these blanks come from; the inventory check is scoped to it.
  function styleHandle() {
    return (listing.sourceStyle && listing.sourceStyle.handle) || '';
  }

  function checkStock() {
    var line = el('stock');
    if (!line) return;

    var color = colorsByKey[selected.color];
    var size = currentOption(SIZE_OPTION);
    if (!color || !size) { line.hidden = true; return; }

    var match = (color.sizes || []).filter(function (s) {
      return String(s.name).toLowerCase() === String(size).toLowerCase();
    })[0];

    if (!match) {
      line.hidden = false;
      line.dataset.state = 'out';
      line.textContent = color.name + ' does not come in ' + size + '.';
      return;
    }

    line.hidden = true;
    if (!window.CCSApi || !window.CCSApi.checkInventory || !match.gtin || !styleHandle()) return;

    window.CCSApi.checkInventory(styleHandle(), match.gtin).then(function (result) {
      // A slower earlier check can land after a later one.
      if (selected.color !== color.key || currentOption(SIZE_OPTION) !== size) return;
      if (result && result.available === false) {
        line.hidden = false;
        line.dataset.state = 'out';
        line.textContent = 'This size is out of stock in ' + color.name + ' right now.';
      }
    }).catch(function () { /* advisory only */ });
  }

  // ── Boot ──────────────────────────────────────────────────────────────────

  function init() {
    root = document.getElementById('ccsp');
    if (!root) return;

    var data = document.getElementById('ccs-listing-data');
    if (!data) return;
    try { listing = JSON.parse(data.textContent); } catch (e) { return; }
    if (!listing || !listing.colors || !listing.colors.length) return;

    listing.colors.forEach(function (color) { colorsByKey[color.key] = color; });

    if (listing.colors.length > 1) hideThemeColorFieldset();

    root.addEventListener('click', function (event) {
      var swatch = event.target.closest('[data-ccsp="swatch"]');
      if (swatch) { ensureSize.touched = false; pickColor(swatch.dataset.color); return; }

    });

    // A shopper choosing a size deliberately stops the Large default
    // re-asserting itself on the next color change.
    document.addEventListener('change', function (event) {
      if (!event.target.matches || !event.target.matches('input.product-form__radio')) return;
      if (event.target.name === SIZE_OPTION) { ensureSize.touched = true; checkStock(); }
      if (event.target.name === COLOR_OPTION) {
        var color = colorFor(event.target.value);
        // Came from the theme's own control (or a deep link), so follow it
        // rather than forwarding back into it.
        if (color && color.key !== selected.color) pickColor(color.key, { fromTheme: true });
      }
    });

    /*
     * A deep link wins over the seller's default: ?variant= names a colour
     * the shopper asked for, and overriding that would be answering a
     * different question.
     *
     * Read off the URL rather than off the form. The theme always has a
     * colour selected -- with no ?variant= Shopify pre-checks the first
     * option value -- so asking the form "did someone choose this?" always
     * said yes, and the default colour was never once used. A seller could
     * set it, see it stored, and still watch their page open on whatever
     * colour happened to be first.
     */
    var deepLinked = /[?&]variant=/.test(window.location.search);
    var themeColor = deepLinked ? colorFor(currentOption(COLOR_OPTION)) : null;
    var start = (themeColor && themeColor.key) ||
      (colorsByKey[root.dataset.defaultColor] && root.dataset.defaultColor) ||
      listing.colors[0].key;

    pickColor(start, { fromTheme: !!themeColor, initial: true });

    /*
     * Hand the gallery back.
     *
     * The page is rendered with everything but the default color's images
     * hidden, so it paints the right ones rather than painting all of them
     * and correcting itself. pickColor has now told the theme which color is
     * selected, and the theme filters the gallery on that -- so the static
     * version can go.
     *
     * Two frames, because the theme's filtering runs off the change event and
     * then refreshes its slider; removing this in the same tick puts the
     * hidden images back for the moment in between.
     */
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        document.documentElement.classList.remove('ccsp-initial-gallery');
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})(window, document);
